import React, { useEffect, useState } from "react";
import API_BASE_URL from "../../../components/apiconfig/api-config";
import NoRecordFound from "../../../components/common/no-record-found";

const currencyFormat = (value) => {
  if (value === undefined || value === null) return "-";
  return Number(value).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  });
};

const LandingCostReport = () => {
  const [columns, setColumns] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contractNumbers, setContractNumbers] = useState([]); // For dropdown
  const [selectedContract, setSelectedContract] = useState(""); // Selected contract
  // Track expanded order per contract
  const [expandedOrders, setExpandedOrders] = useState({}); // { contract_no: order_id }

  // Fetch contract numbers and default report
  useEffect(() => {
    const fetchInitial = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(
          `${API_BASE_URL}/api/report/landing-cost-report`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            credentials: "include",
          }
        );
        if (!res.ok) throw new Error("Failed to fetch report");
        const result = await res.json();
        setContractNumbers(
          Array.isArray(result.contractNumbers) ? result.contractNumbers : []
        );
        // Default: select most recent contract
        const mostRecent =
          result.contractNumbers && result.contractNumbers.length > 0
            ? result.contractNumbers[0]
            : "";
        setSelectedContract(mostRecent);
        setColumns(Array.isArray(result.columns) ? result.columns : []);
        setData(Array.isArray(result.data) ? result.data : []);
        setError(null);
      } catch (err) {
        setColumns([]);
        setData([]);
        setContractNumbers([]);
        setError("Failed to fetch report");
      } finally {
        setLoading(false);
      }
    };
    fetchInitial();
  }, []);

  // Fetch report when selectedContract changes
  useEffect(() => {
    if (!selectedContract) return;
    const fetchReport = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const url = `${API_BASE_URL}/api/report/landing-cost-report?purchase_contract_no=${encodeURIComponent(
          selectedContract
        )}`;
        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          credentials: "include",
        });
        if (!res.ok) throw new Error("Failed to fetch report");
        const result = await res.json();
        setColumns(Array.isArray(result.columns) ? result.columns : []);
        setData(Array.isArray(result.data) ? result.data : []);
        setError(null);
      } catch (err) {
        setColumns([]);
        setData([]);
        setError("Failed to fetch report");
      } finally {
        setLoading(false);
      }
    };
    // Only fetch if not initial load (handled above)
    if (contractNumbers.length > 0) fetchReport();
  }, [selectedContract, contractNumbers.length]);

  // Toggle expand/collapse for an order in a contract
  const handleToggleOrder = (contractNo, orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [contractNo]: prev[contractNo] === orderId ? null : orderId,
    }));
  };

  return (
    <div className="max-w-8xl mx-auto p-6 bg-white  shadow-sm border border-gray-100">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            Landing Cost Report
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Detailed cost breakup and total landed cost per contract (grouped by
            contract number)
          </p>
        </div>
        {/* Dropdown for contract selection */}
        <div className="flex items-center gap-2">
          <label
            htmlFor="contractDropdown"
            className="text-sm font-medium text-gray-700"
          >
            Purchase Contract No:
          </label>
          <select
            id="contractDropdown"
            className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
            value={selectedContract}
            onChange={(e) => setSelectedContract(e.target.value)}
            disabled={loading || contractNumbers.length === 0}
          >
            {contractNumbers.length === 0 && (
              <option value="">No contracts</option>
            )}
            {contractNumbers.map((no) => (
              <option key={no} value={no}>
                {no}
              </option>
            ))}
          </select>
        </div>
      </div>
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 space-y-3">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600"></div>
          <p className="text-sm text-gray-600">Loading landing cost data...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading report
              </h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      ) : data.length === 0 ? (
        <NoRecordFound
          message="No landing cost records found"
          subMessage="Try adjusting your filters or check data availability"
          iconClass="text-gray-400"
        />
      ) : (
        <div className="space-y-8">
          {(data || []).map((contract, idx) => (
            <div
              key={contract?.contract_no || idx}
              className="border border-gray-200 rounded-xl bg-white mb-6 shadow-sm"
            >
              <div className="px-6 py-3 rounded-t-lg flex items-center bg-gray-50 border-b">
                <span className="font-semibold text-lg text-[#3899cf]">
                  Contract No: {contract?.contract_no}
                </span>
              </div>
              <div className="overflow-x-auto p-4">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-gray-700 font-semibold">
                    <tr>
                      <th className="px-4 py-2 text-left">Order ID</th>
                      <th className="px-4 py-2 text-left">Order Type</th>
                      <th className="px-4 py-2 text-left">Order Date</th>
                      <th className="px-4 py-2 text-right">Quantity (MT)</th>
                      <th className="px-4 py-2 text-right">Unit Price (USD)</th>
                      <th className="px-4 py-2 text-left">Incoterm</th>
                    
                      <th className="px-4 py-2 text-right">
                        Total Landed Cost (INR)
                      </th>
                      <th className="px-4 py-2 text-center">Expand</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {(contract?.orders || []).map((order, oidx) => (
                      <React.Fragment key={order?.order_id || oidx}>
                        <tr
                          className={`hover:bg-gray-50 cursor-pointer`}
                          onClick={() =>
                            handleToggleOrder(
                              contract?.contract_no,
                              order?.order_id
                            )
                          }
                        >
                          <td className="px-4 py-3 text-xs text-gray-700 font-medium">
                            {order?.order_id}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-700">
                            {order?.order_type}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-700">
                            {order?.order_date
                              ? new Date(order.order_date).toLocaleDateString()
                              : "-"}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-700 text-right">
                            {order?.quantity_mt}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-700 text-right">
                            {order?.unit_price_usd}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-700">
                            {order?.incoterm}
                          </td>
                          {/* Status cell removed */}
                          <td className="px-4 py-3 text-xs text-gray-700 font-semibold text-right">
                            {currencyFormat(order?.total_landed_cost_inr)}
                          </td>
                          <td className="px-4 py-3 text-center text-gray-600">
                            {expandedOrders[contract?.contract_no] ===
                            order?.order_id
                              ? "▲"
                              : "▼"}
                          </td>
                        </tr>
                        {/* Expandable cost breakup row remains as is */}
                        {expandedOrders[contract?.contract_no] ===
                          order?.order_id && (
                          <tr>
                            <td
                              colSpan={8}
                              className="px-4 pb-4 pt-0 bg-gray-50"
                            >
                              <div className="overflow-x-auto">
                                {(() => {
                                  const nonZeroColumns = (columns || []).filter(
                                    (col) =>
                                      (order?.cost_breakup?.[col] ?? 0) !== 0
                                  );
                                  if (nonZeroColumns.length === 0) {
                                    return (
                                      <div className="text-xs text-gray-500 py-2">
                                        No cost breakup available for this
                                        order.
                                      </div>
                                    );
                                  }
                                  return (
                                    <div className="mb-2">
                                      <div className="font-semibold mb-1 text-xs">
                                        Cost Breakup
                                      </div>
                                      <div className="flex flex-wrap gap-6 mb-2 text-xs text-gray-700">
                                        <div>
                                          <span className="font-semibold text-gray-600">
                                            Order ID:
                                          </span>{" "}
                                          {order?.order_id}
                                        </div>
                                        <div>
                                          <span className="font-semibold text-gray-600">
                                            Total Landed Cost (INR):
                                          </span>{" "}
                                          {currencyFormat(
                                            order?.total_landed_cost_inr
                                          )}
                                        </div>
                                      </div>
                                      <table className="min-w-max divide-y divide-gray-200 border border-gray-200 rounded mt-1">
                                        <thead className="bg-gray-50">
                                          <tr>
                                            {nonZeroColumns.map((col) => (
                                              <th
                                                key={col}
                                                className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                                              >
                                                {col.replace(/_/g, " ")}
                                              </th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          <tr>
                                            {nonZeroColumns.map((col) => (
                                              <td
                                                key={col}
                                                className="px-4 py-2 text-xs text-gray-700 font-medium"
                                              >
                                                {currencyFormat(
                                                  order?.cost_breakup?.[col]
                                                )}
                                              </td>
                                            ))}
                                          </tr>
                                        </tbody>
                                      </table>
                                    </div>
                                  );
                                })()}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    {/* Subtotal row */}
                    <tr className="bg-gray-100">
                      {/* Empty cells for all columns except Total Landed Cost (INR) */}
                      <td colSpan={6}></td>
                      <td className="px-4 py-3 text-xs font-bold text-gray-800 text-right align-middle">
                        Subtotal:{" "}
                        {currencyFormat(
                          contract?.orders?.reduce(
                            (sum, o) =>
                              sum + (Number(o.total_landed_cost_inr) || 0),
                            0
                          )
                        )}
                      </td>
                      <td></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LandingCostReport;
